import type { ChipTone, SelectOption } from "@outpave/ds";
import WebhookEndpointStatus from "enums/WebhookEndpointStatus";

export type WebhookEndpointForm = { url: string; events: string[] };

// Translation keys, so `t()` stays at the call site.
const STATUS_LABEL_KEY: Record<WebhookEndpointStatus, string> = {
  [WebhookEndpointStatus.ACTIVE]: "Active",
  [WebhookEndpointStatus.PAUSED]: "Paused",
  [WebhookEndpointStatus.DELETED]: "Deleted",
};

const STATUS_TONE: Record<WebhookEndpointStatus, ChipTone> = {
  [WebhookEndpointStatus.ACTIVE]: "positive",
  [WebhookEndpointStatus.PAUSED]: "neutral",
  [WebhookEndpointStatus.DELETED]: "neutral",
};

export const webhookEndpointStatusLabelKey = (
  status: WebhookEndpointStatus,
): string => STATUS_LABEL_KEY[status] ?? "Unknown Status";

export const webhookEndpointStatusTone = (
  status: WebhookEndpointStatus,
): ChipTone => STATUS_TONE[status] ?? "neutral";

// "card.frozen" belongs to the "card" object.
export const webhookEventObject = (type: string): string => type.split(".")[0];

export const webhookEventOptions = (types: string[]): SelectOption[] =>
  [...types]
    .sort(
      (a, b) =>
        webhookEventObject(a).localeCompare(webhookEventObject(b)) ||
        a.localeCompare(b),
    )
    .map((type) => ({
      id: type,
      label: type,
      caption: webhookEventObject(type),
    }));

// The server vets the destination; this only saves a round trip.
export const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value.trim()).protocol === "https:";
  } catch {
    return false;
  }
};

// A subscription is a set: picking the same types in another order changes nothing.
export const sameWebhookEvents = (a: string[], b: string[]): boolean => {
  const sorted = [...b].sort();
  return (
    a.length === b.length &&
    [...a].sort().every((type, i) => type === sorted[i])
  );
};

export const hasWebhookEndpointChanges = (
  initial: WebhookEndpointForm,
  current: WebhookEndpointForm,
): boolean =>
  initial.url.trim() !== current.url.trim() ||
  !sameWebhookEvents(initial.events, current.events);
